/**
 * Form fields for an integration's credential or config schema
 * ({key: "required" | "optional"}), shared by the Connect and Manage dialogs so
 * a provider with two keys (Smarty: Auth ID + Auth Token) always shows both.
 */

import i18n from 'i18next'
import { z } from 'zod'
import type { FormField } from '@/shared/lib/forms'

export function schemaFields(schema: Record<string, string>, secret: boolean): FormField[] {
  return Object.keys(schema).map((key) => {
    const label = formatFieldLabel(key)
    return {
      type: secret ? 'password' : 'text',
      name: key,
      label,
      placeholder: secret
        ? i18n.t('integrations.pasteField', 'Paste your {{field}}', { field: label })
        : label,
      value: '',
      validationSchema:
        schema[key] === 'required'
          ? z
              .string()
              .trim()
              .min(1, i18n.t('integrations.fieldRequired', '{{field}} is required.', { field: label }))
          : z.string().trim(),
    }
  })
}

/** Subset of `values` for `keys`; `dropEmpty` skips blank entries (config). */
export function pickKeys(
  values: Record<string, string>,
  keys: string[],
  dropEmpty = false,
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const key of keys) {
    const value = values[key]
    if (value === undefined) continue
    if (dropEmpty && value.trim() === '') continue
    out[key] = value
  }
  return out
}

/** "auth_id" -> "Auth ID", "api_key" -> "API Key" */
export function formatFieldLabel(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bApi\b/g, 'API')
    .replace(/\bId\b/g, 'ID')
}
